<template>
    <div :class="['page-wrapper', 'chiller-theme', 'toggled', themeClass]">
        <SideBar
            ref="sidebar"
            :displayQuotebutton="sideBarProps.displayQuotebutton"
            :isBpaNoquote="sideBarProps.isBpaNoquote"
            @toggleQuoteModal="runSidebarHandler('toggleQuoteModal')"
        />

        <main class="page-content">
            <TopNavbar
                :enable-global-search="topNavbarProps.enableGlobalSearch"
                :shortcut-help-title="shortcutHelpModel.title"
                :shortcut-help-items="shortcutHelpModel.items"
                :theme-mode="themeMode"
                @toggleSidebar="toggleSidebar"
                @toggleTheme="toggleTheme"
                @resetSearch="runTopbarHandler('resetSearch')"
                @copyParts="runTopbarHandler('copyParts')"
                @showParts="runTopbarHandler('showParts')"
                @editParts="runTopbarHandler('editParts')"
                @editCustomerRef="runTopbarHandler('editCustomerRef')"
                @fuzzyPartsMatches="runTopbarHandler('fuzzyPartsMatches')"
                @downloadQuote="runTopbarHandler('downloadQuote')"
                @search="runTopbarHandler('search', $event)"
            />

            <router-view />
        </main>
    </div>
</template>

<script>
    import SideBar from '@/components/shared/SideBar.vue'
    import TopNavbar from '@/components/shared/TopNavbar.vue'
    import config from '@/services/config'
    import { getShortcutHelpForRoute } from '@/utils/shortcutRegistry'

    const storageThemeKey = 'crm-theme-mode'
    const defaultTopNavbarProps = {
        enableGlobalSearch: true,
    }

    const defaultSideBarProps = {
        displayQuotebutton: false,
        isBpaNoquote: false,
    }

    export default {
        name: 'AppShell',
        components: {
            SideBar,
            TopNavbar,
        },
        data() {
            return {
                topbarHandlers: {},
                sidebarHandlers: {},
                topNavbarProps: { ...defaultTopNavbarProps },
                sideBarProps: { ...defaultSideBarProps },
                themeMode: 'light',
            }
        },
        computed: {
            shortcutHelpModel() {
                const shortcutHelp = getShortcutHelpForRoute(this.$route?.name)
                if (!config.isSearchDemo?.()) {
                    return shortcutHelp
                }

                const hiddenHandlers = {
                    dashboard: ['open_quote_modal'],
                    'my-quotes': ['download_quotes'],
                }
                const routeHiddenHandlers = hiddenHandlers[this.$route?.name] || []

                return {
                    ...shortcutHelp,
                    items: shortcutHelp.items.filter(
                        (item) => !routeHiddenHandlers.includes(item.handler)
                    ),
                }
            },
            themeClass() {
                return this.themeMode === 'dark' ? 'theme-dark' : 'theme-light'
            },
        },
        mounted() {
            const storedTheme =
                window.localStorage.getItem(storageThemeKey) ||
                document.documentElement.dataset.theme ||
                'light'
            this.setTheme(storedTheme)
        },
        provide() {
            return {
                appShell: {
                    setTopbarHandlers: this.setTopbarHandlers,
                    setSidebarHandlers: this.setSidebarHandlers,
                    setTopNavbarProps: this.setTopNavbarProps,
                    setSideBarProps: this.setSideBarProps,
                    resetLayout: this.resetLayout,
                },
            }
        },
        methods: {
            toggleSidebar() {
                this.$refs.sidebar?.toggleSidebar()
            },
            setTopbarHandlers(handlers = {}) {
                this.topbarHandlers = { ...handlers }
            },
            setSidebarHandlers(handlers = {}) {
                this.sidebarHandlers = { ...handlers }
            },
            setTopNavbarProps(props = {}) {
                this.topNavbarProps = {
                    ...defaultTopNavbarProps,
                    ...props,
                }
            },
            setSideBarProps(props = {}) {
                this.sideBarProps = {
                    ...defaultSideBarProps,
                    ...props,
                }
            },
            runTopbarHandler(handlerName, ...args) {
                const handler = this.topbarHandlers?.[handlerName]
                if (typeof handler === 'function') {
                    handler(...args)
                }
            },
            runSidebarHandler(handlerName, ...args) {
                const handler = this.sidebarHandlers?.[handlerName]
                if (typeof handler === 'function') {
                    handler(...args)
                }
            },
            setTheme(mode = 'light') {
                const nextMode = mode === 'dark' ? 'dark' : 'light'
                this.themeMode = nextMode
                document.documentElement.dataset.theme = nextMode
                document.documentElement.classList.toggle('theme-dark', nextMode === 'dark')
                document.documentElement.classList.toggle('theme-light', nextMode === 'light')
                window.localStorage.setItem(storageThemeKey, nextMode)
            },
            toggleTheme() {
                this.setTheme(this.themeMode === 'dark' ? 'light' : 'dark')
            },
            resetLayout() {
                this.topbarHandlers = {}
                this.sidebarHandlers = {}
                this.topNavbarProps = { ...defaultTopNavbarProps }
                this.sideBarProps = { ...defaultSideBarProps }
            },
        },
    }
</script>
